import React, { useState, useEffect } from 'react'
import { backendService, DoctorCreate } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import './DoctorRegistration.css'

interface FormData {
  name: string
  username: string
  password: string
  confirmPassword: string
  email: string
  phone: string
  specialization: string
}

interface ValidationErrors {
  name?: string
  username?: string
  password?: string
  confirmPassword?: string
  email?: string
}

export const DoctorRegistration: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    phone: '',
    specialization: 'General Medicine'
  })

  const [errors, setErrors] = useState<ValidationErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [successDoctor, setSuccessDoctor] = useState<{ name: string; username: string } | null>(null)

  // Load draft from sessionStorage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('doctorRegistrationDraft')
      if (saved) {
        const parsed = JSON.parse(saved)
        setFormData(prev => ({ ...prev, ...parsed }))
        logInfo('Loaded doctor registration draft from session storage', 'DoctorRegistration')
      }
    } catch (error) {
      logError('Failed to load draft from session storage', 'DoctorRegistration')
    }
  }, [])

  // Save draft to sessionStorage on change
  useEffect(() => {
    try {
      // Don't save passwords to session storage for security
      const draftData = { ...formData, password: '', confirmPassword: '' }
      sessionStorage.setItem('doctorRegistrationDraft', JSON.stringify(draftData))
    } catch (error) {
      logError('Failed to save draft to session storage', 'DoctorRegistration')
    }
  }, [formData])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error for this field when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }

    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError(null)
    }
  }

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required'
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters'
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters'
    } else if (formData.username.trim().length > 20) {
      newErrors.username = 'Username must be at most 20 characters'
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username.trim())) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores'
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter'
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number'
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    // Email validation (optional but must be valid if provided)
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      logError('Form validation failed', 'DoctorRegistration')
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      // Prepare data in the format expected by the backend
      const doctorData: DoctorCreate = {
        name: formData.name.trim(),
        specialization: formData.specialization.trim() || 'General Medicine',
        contactInfo: {
          email: formData.email.trim() || '',
          phone: formData.phone.trim() || ''
        },
        credentials: {
          username: formData.username.trim(),
          password: formData.password
        }
      }

      logInfo(`Registering new doctor: ${doctorData.name} (username: ${doctorData.credentials?.username})`, 'DoctorRegistration')

      const newDoctor = await backendService.addDoctor(doctorData)

      logInfo(`Doctor registered successfully: ${newDoctor.name} (ID: ${newDoctor.doctorId})`, 'DoctorRegistration')

      setSubmitSuccess(true)
      setSubmitError(null)
      setSuccessDoctor({ name: newDoctor.name, username: doctorData.credentials?.username || '' })

      // Auto-close modal and reset form after 5 seconds
      setTimeout(() => {
        handleCloseSuccess()
      }, 5000)

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to register doctor'
      logError(`Doctor registration failed: ${errorMessage}`, 'DoctorRegistration')
      setSubmitError(errorMessage)
      setSubmitSuccess(false)
      setSuccessDoctor(null)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCloseSuccess = () => {
    setSubmitSuccess(false)
    setSuccessDoctor(null)
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      name: '',
      username: '',
      password: '',
      confirmPassword: '',
      email: '',
      phone: '',
      specialization: 'General Medicine'
    })
    setErrors({})
    setSubmitError(null)
    sessionStorage.removeItem('doctorRegistrationDraft')
    logInfo('Doctor registration form reset', 'DoctorRegistration')
  }

  return (
    <div className="doctor-registration">
      <form onSubmit={handleSubmit} className="doctor-registration-form">
        {/* Error Message */}
        {submitError && (
          <div className="alert alert-error">
            <span className="alert-icon">!</span>
            <div className="alert-content">
              <strong>Error:</strong> {submitError}
            </div>
          </div>
        )}

        {/* Success Modal */}
        {submitSuccess && successDoctor && (
          <div className="success-overlay" role="dialog" aria-modal="true" aria-labelledby="success-modal-title">
            <div className="success-modal">
              <div className="success-icon">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h2 id="success-modal-title">Registration Successful</h2>
              <p className="success-subtitle">New doctor has been registered to the platform.</p>
              <div className="success-doctor-details">
                <div className="detail-row">
                  <span className="detail-label">Name</span>
                  <span className="detail-value">{successDoctor.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Username</span>
                  <span className="detail-value">@{successDoctor.username}</span>
                </div>
              </div>
              <button type="button" className="btn-ok" onClick={handleCloseSuccess}>
                OK
              </button>
            </div>
          </div>
        )}

        {/* Personal Information Section */}
        <div className="form-section">
          <h3 className="form-section-title">Personal Information</h3>
          
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Full Name <span className="required">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              className={`form-input ${errors.name ? 'error' : ''}`}
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Dr. John Smith"
              disabled={isSubmitting}
              autoFocus
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="specialization" className="form-label">
              Specialization
            </label>
            <select
              id="specialization"
              name="specialization"
              className="form-input"
              value={formData.specialization}
              onChange={handleInputChange}
              disabled={isSubmitting}
            >
              <option value="General Medicine">General Medicine</option>
              <option value="Orthopedics">Orthopedics</option>
              <option value="Podiatry">Podiatry</option>
              <option value="Physical Therapy">Physical Therapy</option>
              <option value="Sports Medicine">Sports Medicine</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        {/* Account Credentials Section */}
        <div className="form-section">
          <h3 className="form-section-title">Account Credentials</h3>
          
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username <span className="required">*</span>
            </label>
            <input
              id="username"
              name="username"
              type="text"
              className={`form-input ${errors.username ? 'error' : ''}`}
              value={formData.username}
              onChange={handleInputChange}
              placeholder="john_smith"
              disabled={isSubmitting}
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
            <span className="form-hint">3-20 characters, letters, numbers, and underscores only</span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password <span className="required">*</span>
              </label>
              <input
                id="password"
                name="password"
                type="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={formData.password}
                onChange={handleInputChange}
                placeholder="••••••••"
                disabled={isSubmitting}
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password <span className="required">*</span>
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="••••••••"
                disabled={isSubmitting}
              />
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>
          </div>
          <span className="form-hint">Min 8 characters, 1 uppercase, 1 number</span>
        </div>

        {/* Contact Information Section */}
        <div className="form-section">
          <h3 className="form-section-title">Contact Information (Optional)</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john.smith@example.com"
                disabled={isSubmitting}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                className="form-input"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 (555) 123-4567"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={resetForm}
            disabled={isSubmitting}
          >
            Reset Form
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Registering...
              </>
            ) : (
              'Register Doctor'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

